import { BrowserRouter, useLocation } from 'react-router-dom';
import { isLoggedIn } from '@/utils/user';
import { useEffect, useState } from 'react';
import { PaletteMode, ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { NavDrawer } from './navigation/NavDrawer';
import { enqueueSnackbar, SnackbarProvider } from 'notistack';
import { RoutesSection } from './navigation/RoutesSection';
import { WTFAppBar } from './navigation/WTFAppBar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AppContext, AppContextState } from '@/contexts/app.context';
import { useBoneTagMap } from './hooks/use-skeleton-tag-map.hook';
import { useUserProfile } from './hooks/user-user-profile.hook';
import { sync } from './contexts/crossTabStore';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const getDesignTokens = (mode: PaletteMode) => ({
  typography: {
    fontFamily: [
      'Quicksand',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
  },
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          primary: {
            main: '#696C3A',
          },
          secondary: {
            main: '#606c38',
          },
          background: {
            default: 'grey',
            paper: '#283618',
          },
          text: {
            primary: 'rgba(255, 255, 255, 1.0)',
            secondary: 'rgba(255, 255, 255, 1.0)',
            disabled: 'grey',
          },
          action: {
            active: 'rgba(255, 255, 255, 1.0)', // 👈 default icon color
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(0, 0, 0, 0.08)',
            disabled: 'rgba(0, 0, 0, 0.26)',
          },
        }
      : {
          // palette values for dark mode
          primary: {
            main: '#696C3A',
          },
          secondary: {
            main: '#ce93d8',
          },
          background: {
            default: '#121212',
            paper: '#1e1e1e',
          },
          text: {
            primary: '#ffffff',
            secondary: '#bbbbbb',
          },
        }),
  },
});

const theme = createTheme(getDesignTokens('dark') as any);

export const queryClient = new QueryClient();

function App() {
  const { boneTagList, boneTagMap, boneTagLoadError, loadBoneTagMap } = useBoneTagMap();
  const { user, error, refresh: refreshUserProfile } = useUserProfile();

  const [state, setState] = useState<AppContextState>();

  const [extraTitle, setExtraTitle] = useState<string>();
  const [fileCache, setFileCache] = useState<Map<string, string>>(new Map<string, string>());

  useEffect(() => {
    if (isLoggedIn()) {
      if (boneTagLoadError) enqueueSnackbar(`Failed to load Bone Tags: ${boneTagLoadError}`, { variant: 'error' });
    }
  }, [boneTagLoadError]);

  useEffect(() => {
    const newState = {
      userProfile: user,
      boneTagList,
      boneTagMap,
    };
    setState(newState);
    sync.post({ type: 'SET', key: 'state', value: newState });
  }, [user, extraTitle]);

  useEffect(() => {
    return sync.onMessage((msg) => {
      if (msg.type === 'SET' && msg.key === 'state') {
        setState(msg.value);
      }
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider
        value={{
          extraTitle,
          state,
          refreshUserProfile: refreshUserProfile,
          setExtraTitle: (t) => setExtraTitle(t),
          fileCache,
          refreshBoneTagList: loadBoneTagMap,
          setFileCache,
        }}
      >
        <SnackbarProvider maxSnack={3}>
          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en">
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <BrowserRouter basename={'/'}>
                <Drawer />
              </BrowserRouter>
            </ThemeProvider>
          </LocalizationProvider>
        </SnackbarProvider>
      </AppContext.Provider>
    </QueryClientProvider>
  );
}

const Drawer = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <>
      <NavDrawer setDrawerOpen={setDrawerOpen} open={drawerOpen} />
      {pathname !== '/' && pathname !== '/login' && <WTFAppBar setDrawerOpen={setDrawerOpen} drawerOpen={drawerOpen} />}
      <RoutesSection />
    </>
  );
};

export default App;
